import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Users, Trophy, ShieldCheck, Megaphone, Handshake } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHero from "@/components/PageHero";
import PartnersMarquee from "@/components/PartnersMarquee";
import VolunteerForm from "@/components/VolunteerForm";

const ECOSYSTEM = [
  { icon: Building2, t: "GOVERNMENT & TOURISM AUTHORITIES", d: "Administrative support, destination promotion and event coordination." },
  { icon: Users, t: "LOCAL COMMUNITIES", d: "Schools, colleges, NGOs, clubs and community organisations." },
  { icon: Trophy, t: "SPORTS ORGANISATIONS", d: "Athletic bodies, sports clubs and local sporting communities." },
  { icon: ShieldCheck, t: "SECURITY & FIELD SUPPORT", d: "Police, defence organisations, volunteers and local agencies where required." },
  { icon: Handshake, t: "SPONSORS & BRANDS", d: "Brands looking to connect with active, travel-oriented and community-driven audiences." },
  { icon: Megaphone, t: "MEDIA", d: "Event promotion, storytelling, digital reach and destination visibility." },
];

const OPPORTUNITIES = ["EVENT PARTNERSHIP", "DESTINATION PROMOTION", "TITLE / PRESENTING PARTNERSHIP", "BRAND ACTIVATION", "SPORTS TOURISM PROJECTS", "COMMUNITY INITIATIVES", "MEDIA PARTNERSHIP"];

export default function Partners() {
  const [params] = useSearchParams();
  const sponsorFor = params.get("sponsor");
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", type: sponsorFor ? "Sponsorship" : "Partnership", message: sponsorFor ? `Interested in sponsoring the event: ${sponsorFor}` : "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! Our partnerships team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", organization: "", type: "Partnership", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="partners-page">
      <PageHero eyebrow="PARTNERS & IMPACT" title="Building Destinations Through Collaboration."
        subtitle="SPORTIVE's event ecosystem brings together governments, communities, sports bodies, brands and media to create destination experiences that last far beyond race day."
        image="https://images.unsplash.com/photo-1613936360976-8f35cf0e5461?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxtYXJhdGhvbiUyMHJ1bm5lcnMlMjByYWNlJTIwc3RhcnR8ZW58MHx8fHwxNzg5MDY4MTkwfDA&ixlib=rb-4.1.0&q=85" />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <p className="eyebrow">THE ECOSYSTEM</p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ECOSYSTEM.map((e) => (
            <div key={e.t} className="card-k p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-cyan/40">
              <e.icon size={24} className="text-brand-cyan" />
              <h3 className="mt-4 font-display text-lg font-bold tracking-wide text-white">{e.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{e.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-brand-line bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="eyebrow">PARTNER WITH SPORTIVE</p>
          <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold uppercase tracking-tight text-white">Let's Create the Next Destination Run.</h2>
          <p className="mt-4 max-w-2xl text-slate-400">Whether you represent a tourism department, government organisation, corporate brand, destination, hotel, sports organisation, NGO or media house — SPORTIVE can work with you to develop a destination-based sporting experience.</p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {OPPORTUNITIES.map((o) => (
              <span key={o} className="rounded-full border border-brand-olive/40 bg-brand-olive/10 px-4 py-2 font-mono text-[11px] tracking-[0.12em] text-brand-olive">{o}</span>
            ))}
          </div>
        </div>
      </section>

      <PartnersMarquee eyebrow="PREVIOUS PARTNERS & COLLABORATORS" title="The Ecosystem Behind Our Events" />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">SPONSORSHIP OPPORTUNITIES</p>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">Sponsorship Deck & Deliverables</h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-400">Put your brand on India's most scenic start lines. Every SPORTIVE partnership comes with clearly defined deliverables — on-ground, on-kit and on-screen.</p>
          </div>
          <a data-testid="download-deck" href="/sponsorship-deck.pdf" target="_blank" rel="noreferrer" className="btn-kinetic btn-primary-k">DOWNLOAD DECK (PDF)</a>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {[
            { name: "TITLE / PRESENTING PARTNER", c: "border-brand-cyan/50", tc: "text-brand-cyan", tag: "MAXIMUM VISIBILITY",
              dels: ["Event naming rights — 'presented by Your Brand'", "Largest logo on backdrop, start/finish gantry & medals", "Logo on T-shirt front, bibs & certificates", "Event film, photos & aftermovie branding", "Homepage + event page presence", "Dedicated social campaign & PR", "Prime activation stall & expo space", "VIP hospitality & ceremony stage time", "First right of refusal for next edition"] },
            { name: "GOLD / POWERED BY", c: "border-brand-olive/50", tc: "text-brand-olive", tag: "HIGH VISIBILITY",
              dels: ["Main backdrop & gantry logo", "T-shirt back & hydration station branding", "Event page + website sponsor section", "Social posts & event film logo", "Activation stall in finish zone", "VIP passes & ceremony mention"] },
            { name: "SILVER / ASSOCIATE", c: "border-brand-line", tc: "text-white", tag: "STRONG PRESENCE",
              dels: ["Main backdrop & route signage logo", "Finisher certificate branding", "Website sponsor listing", "Social media mentions", "Race-day announcements"] },
            { name: "SUPPORT / COMMUNITY", c: "border-brand-line", tc: "text-white", tag: "ECOSYSTEM",
              dels: ["Event page logo", "Venue signage", "Social media mention", "Community & volunteer engagement"] },
          ].map((t) => (
            <div key={t.name} className={`card-k flex flex-col border p-6 ${t.c}`}>
              <p className={`font-mono text-[9px] tracking-[0.25em] ${t.tc}`}>{t.tag}</p>
              <h3 className="mt-2 font-display text-xl font-bold tracking-wide text-white">{t.name}</h3>
              <ul className="mt-4 flex-1 space-y-2">
                {t.dels.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-xs leading-relaxed text-slate-300">
                    <span className="mt-0.5 font-mono text-brand-olive">+</span>{d}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-500">Custom packages, in-kind partnerships (hydration, nutrition, gear, travel, hospitality) and multi-event season partnerships available on request. Full deliverables matrix in the downloadable deck.</p>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <p className="eyebrow">START THE CONVERSATION</p>
        <h2 className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-white">Partnership Enquiry</h2>
        <form onSubmit={submit} className="card-k mt-8 space-y-4 p-7">
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="partner-name" required placeholder="Your name" className="input-k" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="partner-org" placeholder="Organisation" className="input-k" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="partner-email" required type="email" placeholder="Email" className="input-k" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="partner-phone" placeholder="Phone" className="input-k" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <select data-testid="partner-type" className="input-k" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {["Partnership", "Sponsorship", "Destination Project", "Media", "Other"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <textarea data-testid="partner-message" required rows={4} placeholder="Tell us about your destination, brand or idea..." className="input-k" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button data-testid="partner-submit" disabled={busy} className="btn-kinetic btn-primary-k w-full justify-center disabled:opacity-60">{busy ? "Sending..." : "PARTNER WITH US"}</button>
        </form>
      </section>
      <section id="volunteer" className="border-t border-brand-line bg-brand-surface/40">
        <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
          <p className="eyebrow">VOLUNTEER WITH SPORTIVE</p>
          <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">Be Part of Race Day.</h2>
          <p className="mt-4 text-slate-400">Volunteers power every SPORTIVE event — route marshals, hydration crews, registration desks, media teams and finish-line cheer squads. Pick an event and join the crew.</p>
          <div className="card-k mt-8 p-7"><VolunteerForm /></div>
        </div>
      </section>
    </div>
  );
}
